// ==========================================================
// I18n Service - Language State & Translation Manager
// ==========================================================

import { TRANSLATIONS, type Language, type TranslationSchema } from './translations';

export type LanguageChangeListener = (lang: Language) => void;

class I18nManager {
  private currentLang: Language = 'ja';
  private listeners: Set<LanguageChangeListener> = new Set();

  constructor() {
    const saved = localStorage.getItem('paint_meister_lang') as Language;
    if (saved === 'ja' || saved === 'en') {
      this.currentLang = saved;
    } else {
      // Auto-detect browser language
      const navLang = navigator.language.toLowerCase();
      this.currentLang = navLang.startsWith('ja') ? 'ja' : 'en';
    }
  }

  public getLanguage(): Language {
    return this.currentLang;
  }

  public setLanguage(lang: Language): void {
    if (this.currentLang === lang) return;
    this.currentLang = lang;
    localStorage.setItem('paint_meister_lang', lang);
    this.updateHtmlLangAttribute();
    this.notifyListeners();
  }

  public toggleLanguage(): Language {
    const nextLang: Language = this.currentLang === 'ja' ? 'en' : 'ja';
    this.setLanguage(nextLang);
    return nextLang;
  }

  public subscribe(listener: LanguageChangeListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    this.listeners.forEach(fn => {
      try {
        fn(this.currentLang);
      } catch (err) {
        console.error('Error in i18n listener:', err);
      }
    });
  }

  public get dict(): TranslationSchema {
    return TRANSLATIONS[this.currentLang];
  }

  public updateHtmlLangAttribute(): void {
    document.documentElement.lang = this.currentLang;
  }
}

export const i18n = new I18nManager();
